import React, { Suspense, lazy, Component } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { PageTransitionWrapper } from '../../shared/ui/PageTransitionWrapper';
import { ProtectedRoute } from './ProtectedRoute';
import { LoadingScreen } from '../../shared/ui/LoadingScreen';
import { Login } from '../../features/auth/pages/Login';
// Lazy-loaded feature pages. Named exports are mapped to default for lazy.
const lazyNamed = <T extends Record<string, unknown>,>(loader: () => Promise<T>, name: keyof T) => lazy(() => loader().then(m => ({
  default: m[name] as ComponentType<unknown>
})));
// analytics
const Overview = lazyNamed(() => import('../../features/analytics'), 'Overview');
const CustomMetrics = lazyNamed(() => import('../../features/analytics'), 'CustomMetrics');
const Nadi = lazyNamed(() => import('../../features/analytics'), 'Nadi');
const Reports = lazyNamed(() => import('../../features/analytics'), 'Reports');
const CardMonitoringPrograms = lazyNamed(() => import('../../features/analytics'), 'CardMonitoringPrograms');
const VampDetail = lazyNamed(() => import('../../features/analytics'), 'VampDetail');
const PaymentAnalytics = lazyNamed(() => import('../../features/analytics'), 'PaymentAnalytics');
// notifications
const Notifications = lazyNamed(() => import('../../features/notifications'), 'Notifications');
// payments
const PaymentRequests = lazyNamed(() => import('../../features/payments'), 'PaymentRequests');
const BulkPayments = lazyNamed(() => import('../../features/payments'), 'BulkPayments');
const BulkPaymentDetail = lazyNamed(() => import('../../features/payments'), 'BulkPaymentDetail');
const VirtualTerminal = lazyNamed(() => import('../../features/payments'), 'VirtualTerminal');
const QRCodes = lazyNamed(() => import('../../features/payments'), 'QRCodes');
// settings
const Settings = lazyNamed(() => import('../../features/settings'), 'Settings');
const UpdatePayoutAccount = lazyNamed(() => import('../../features/settings'), 'UpdatePayoutAccount');
// balance
const PayoutSettings = lazyNamed(() => import('../../features/balance'), 'PayoutSettings');
const BalanceSummary = lazyNamed(() => import('../../features/balance'), 'BalanceSummary');
const BalanceActivity = lazyNamed(() => import('../../features/balance'), 'BalanceActivity');
const BalanceActivityDetail = lazyNamed(() => import('../../features/balance'), 'BalanceActivityDetail');
const Payouts = lazyNamed(() => import('../../features/balance'), 'Payouts');
const PayoutDetail = lazyNamed(() => import('../../features/balance'), 'PayoutDetail');
// terminals
const PhysicalTerminals = lazyNamed(() => import('../../features/terminals'), 'PhysicalTerminals');
const SoftPosTerminals = lazyNamed(() => import('../../features/terminals'), 'SoftPosTerminals');
const Connect = lazyNamed(() => import('../../features/terminals'), 'Connect');
// developers
const WebhookEndpoints = lazyNamed(() => import('../../features/developers'), 'WebhookEndpoints');
const WebhookLogs = lazyNamed(() => import('../../features/developers'), 'WebhookLogs');
// tutorials
const Tutorials = lazyNamed(() => import('../../features/tutorials'), 'Tutorials');
const TutorialDetail = lazyNamed(() => import('../../features/tutorials'), 'TutorialDetail');
// shops (orders, shops, campaigns, sales, inventory)
const Orders = lazyNamed(() => import('../../features/shops'), 'Orders');
const OrderDetail = lazyNamed(() => import('../../features/shops'), 'OrderDetail');
const Shops = lazyNamed(() => import('../../features/shops'), 'Shops');
const ShopDetails = lazyNamed(() => import('../../features/shops'), 'ShopDetails');
const Campaigns = lazyNamed(() => import('../../features/shops'), 'Campaigns');
const CampaignDetails = lazyNamed(() => import('../../features/shops'), 'CampaignDetails');
const CampaignForm = lazyNamed(() => import('../../features/shops'), 'CampaignForm');
const Inventory = lazyNamed(() => import('../../features/shops'), 'Inventory');
const Sales = lazyNamed(() => import('../../features/shops'), 'Sales');
const SaleDetail = lazyNamed(() => import('../../features/shops'), 'SaleDetail');
const ShopSettings = lazyNamed(() => import('../../features/shops'), 'ShopSettings');
const ProductDetail = lazyNamed(() => import('../../features/shops'), 'ProductDetail');
const ProductEdit = lazyNamed(() => import('../../features/shops'), 'ProductEdit');
// rooms
const PropertyInformation = lazyNamed(() => import('../../features/rooms'), 'PropertyInformation');
const Rooms = lazyNamed(() => import('../../features/rooms'), 'Rooms');
const AddRoom = lazyNamed(() => import('../../features/rooms'), 'AddRoom');
const EditRoom = lazyNamed(() => import('../../features/rooms'), 'EditRoom');
const RoomDetail = lazyNamed(() => import('../../features/rooms'), 'RoomDetail');
const Rates = lazyNamed(() => import('../../features/rooms'), 'Rates');
const Reservations = lazyNamed(() => import('../../features/rooms'), 'Reservations');
const ReservationDetail = lazyNamed(() => import('../../features/rooms'), 'ReservationDetail');
// invoices
const Invoices = lazyNamed(() => import('../../features/invoices'), 'Invoices');
const InvoiceDetail = lazyNamed(() => import('../../features/invoices'), 'InvoiceDetail');
const InvoiceForm = lazyNamed(() => import('../../features/invoices'), 'InvoiceForm');
// outlets
const PortfolioDashboard = lazyNamed(() => import('../../features/outlets'), 'PortfolioDashboard');
const OutletDirectory = lazyNamed(() => import('../../features/outlets'), 'OutletDirectory');
const MallView = lazyNamed(() => import('../../features/outlets'), 'MallView');
const OutletDetail = lazyNamed(() => import('../../features/outlets'), 'OutletDetail');
const Reconciliation = lazyNamed(() => import('../../features/outlets'), 'Reconciliation');
const OutletReports = lazyNamed(() => import('../../features/outlets'), 'OutletReports');
const OutletOnboarding = lazyNamed(() => import('../../features/outlets'), 'OutletOnboarding');
// customers
const Customers = lazyNamed(() => import('../../features/customers'), 'Customers');
const CustomerDetail = lazyNamed(() => import('../../features/customers'), 'CustomerDetail');
const CustomerForm = lazyNamed(() => import('../../features/customers'), 'CustomerForm');
// suppliers
const Suppliers = lazyNamed(() => import('../../features/suppliers'), 'Suppliers');
const SupplierDetail = lazyNamed(() => import('../../features/suppliers'), 'SupplierDetail');
const SupplierForm = lazyNamed(() => import('../../features/suppliers'), 'SupplierForm');
const SupplierSelfService = lazyNamed(() => import('../../features/suppliers'), 'SupplierSelfService');
const BulkSupplierUpload = lazyNamed(() => import('../../features/suppliers'), 'BulkSupplierUpload');
// bills
const Bills = lazyNamed(() => import('../../features/bills'), 'Bills');
const BillDetail = lazyNamed(() => import('../../features/bills'), 'BillDetail');
const BillForm = lazyNamed(() => import('../../features/bills'), 'BillForm');
const BillApprovalWizard = lazyNamed(() => import('../../features/bills'), 'BillApprovalWizard');
const BillFinancingWizard = lazyNamed(() => import('../../features/bills'), 'BillFinancingWizard');
const BulkBillUpload = lazyNamed(() => import('../../features/bills'), 'BulkBillUpload');
const Financing = lazyNamed(() => import('../../features/bills'), 'Financing');
const FacilityDetail = lazyNamed(() => import('../../features/bills'), 'FacilityDetail');
const FinancingApplyWizard = lazyNamed(() => import('../../features/bills'), 'FinancingApplyWizard');
// disputes
const Disputes = lazyNamed(() => import('../../features/disputes'), 'Disputes');
const DisputeDetail = lazyNamed(() => import('../../features/disputes'), 'DisputeDetail');
const ChallengeDispute = lazyNamed(() => import('../../features/disputes'), 'ChallengeDispute');
// refunds
const Refunds = lazyNamed(() => import('../../features/refunds'), 'Refunds');
const RefundDetail = lazyNamed(() => import('../../features/refunds'), 'RefundDetail');
// system
const ActivityLogs = lazyNamed(() => import('../../features/system'), 'ActivityLogs');
const Requests = lazyNamed(() => import('../../features/system'), 'Requests');
const RequestDetail = lazyNamed(() => import('../../features/system'), 'RequestDetail');
export function AppRouter() {
  return <BrowserRouter data-id="element-246">
      <Suspense fallback={<LoadingScreen data-id="element-248" />} data-id="element-247">
        <Routes data-id="element-249">
          <Route path="/" element={<Login data-id="element-251" />} data-id="element-250" />
          <Route path="/supplier-onboarding/:id" element={<SupplierSelfService data-id="element-253" />} data-id="element-252" />

          <Route path="/overview" element={<ProtectedRoute data-id="element-255">
                <AppLayout data-id="element-256">
                  <PageTransitionWrapper data-id="element-257">
                    <Overview data-id="element-258" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-254" />
          <Route path="/notifications" element={<ProtectedRoute data-id="element-260">
                <AppLayout data-id="element-261">
                  <PageTransitionWrapper data-id="element-262">
                    <Notifications data-id="element-263" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-259" />
          <Route path="/payment-requests" element={<ProtectedRoute data-id="element-265">
                <AppLayout data-id="element-266">
                  <PageTransitionWrapper data-id="element-267">
                    <PaymentRequests data-id="element-268" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-264" />
          <Route path="/qr-codes" element={<ProtectedRoute data-id="element-270">
                <AppLayout data-id="element-271">
                  <PageTransitionWrapper data-id="element-272">
                    <QRCodes data-id="element-273" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-269" />
          <Route path="/bulk-payments" element={<ProtectedRoute data-id="element-275">
                <AppLayout data-id="element-276">
                  <PageTransitionWrapper data-id="element-277">
                    <BulkPayments data-id="element-278" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-274" />
          <Route path="/bulk-payment-detail/:id" element={<ProtectedRoute data-id="element-280">
                <AppLayout data-id="element-281">
                  <PageTransitionWrapper data-id="element-282">
                    <BulkPaymentDetail data-id="element-283" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-279" />
          <Route path="/settings" element={<ProtectedRoute data-id="element-285">
                <AppLayout data-id="element-286">
                  <PageTransitionWrapper data-id="element-287">
                    <Settings data-id="element-288" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-284" />
          <Route path="/settings/payouts" element={<ProtectedRoute data-id="element-290">
                <AppLayout data-id="element-291">
                  <PageTransitionWrapper data-id="element-292">
                    <PayoutSettings data-id="element-293" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-289" />
          <Route path="/settings/payout-account/edit" element={<ProtectedRoute data-id="element-295">
                <AppLayout data-id="element-296">
                  <PageTransitionWrapper data-id="element-297">
                    <UpdatePayoutAccount data-id="element-298" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-294" />
          <Route path="/virtual-terminal" element={<ProtectedRoute data-id="element-300">
                <AppLayout data-id="element-301">
                  <PageTransitionWrapper data-id="element-302">
                    <VirtualTerminal data-id="element-303" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-299" />
          <Route path="/physical-terminals" element={<ProtectedRoute data-id="element-305">
                <AppLayout data-id="element-306">
                  <PageTransitionWrapper data-id="element-307">
                    <PhysicalTerminals data-id="element-308" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-304" />
          <Route path="/softpos-terminals" element={<ProtectedRoute data-id="element-310">
                <AppLayout data-id="element-311">
                  <PageTransitionWrapper data-id="element-312">
                    <SoftPosTerminals data-id="element-313" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-309" />
          <Route path="/connect" element={<ProtectedRoute data-id="element-315">
                <AppLayout data-id="element-316">
                  <PageTransitionWrapper data-id="element-317">
                    <Connect data-id="element-318" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-314" />
          <Route path="/webhooks/endpoints" element={<ProtectedRoute data-id="element-320">
                <AppLayout data-id="element-321">
                  <PageTransitionWrapper data-id="element-322">
                    <WebhookEndpoints data-id="element-323" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-319" />
          <Route path="/webhooks/logs" element={<ProtectedRoute data-id="element-325">
                <AppLayout data-id="element-326">
                  <PageTransitionWrapper data-id="element-327">
                    <WebhookLogs data-id="element-328" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-324" />
          <Route path="/tutorials" element={<ProtectedRoute data-id="element-330">
                <AppLayout data-id="element-331">
                  <PageTransitionWrapper data-id="element-332">
                    <Tutorials data-id="element-333" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-329" />
          <Route path="/tutorial/:id" element={<ProtectedRoute data-id="element-335">
                <AppLayout data-id="element-336">
                  <PageTransitionWrapper data-id="element-337">
                    <TutorialDetail data-id="element-338" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-334" />
          <Route path="/orders" element={<ProtectedRoute data-id="element-340">
                <AppLayout data-id="element-341">
                  <PageTransitionWrapper data-id="element-342">
                    <Orders data-id="element-343" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-339" />
          <Route path="/order-detail/:id" element={<ProtectedRoute data-id="element-345">
                <AppLayout data-id="element-346">
                  <PageTransitionWrapper data-id="element-347">
                    <OrderDetail data-id="element-348" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-344" />
          <Route path="/shops" element={<ProtectedRoute data-id="element-350">
                <AppLayout data-id="element-351">
                  <PageTransitionWrapper data-id="element-352">
                    <Shops data-id="element-353" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-349" />
          <Route path="/shop-details" element={<ProtectedRoute data-id="element-355">
                <AppLayout data-id="element-356">
                  <PageTransitionWrapper data-id="element-357">
                    <ShopDetails data-id="element-358" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-354" />
          <Route path="/shop-settings/:shopId" element={<ProtectedRoute data-id="element-360">
                <AppLayout data-id="element-361">
                  <PageTransitionWrapper data-id="element-362">
                    <ShopSettings data-id="element-363" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-359" />
          <Route path="/shop-settings" element={<ProtectedRoute data-id="element-365">
                <AppLayout data-id="element-366">
                  <PageTransitionWrapper data-id="element-367">
                    <ShopSettings data-id="element-368" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-364" />
          <Route path="/product-detail/:productId" element={<ProtectedRoute data-id="element-370">
                <AppLayout data-id="element-371">
                  <PageTransitionWrapper data-id="element-372">
                    <ProductDetail data-id="element-373" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-369" />
          <Route path="/product-edit/:productId" element={<ProtectedRoute data-id="element-375">
                <AppLayout data-id="element-376">
                  <PageTransitionWrapper data-id="element-377">
                    <ProductEdit data-id="element-378" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-374" />
          <Route path="/campaigns" element={<ProtectedRoute data-id="element-380">
                <AppLayout data-id="element-381">
                  <PageTransitionWrapper data-id="element-382">
                    <Campaigns data-id="element-383" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-379" />
          <Route path="/campaign-details" element={<ProtectedRoute data-id="element-385">
                <AppLayout data-id="element-386">
                  <PageTransitionWrapper data-id="element-387">
                    <CampaignDetails data-id="element-388" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-384" />
          <Route path="/campaign-form" element={<ProtectedRoute data-id="element-390">
                <AppLayout data-id="element-391">
                  <PageTransitionWrapper data-id="element-392">
                    <CampaignForm data-id="element-393" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-389" />
          <Route path="/property-information" element={<ProtectedRoute data-id="element-395">
                <AppLayout data-id="element-396">
                  <PageTransitionWrapper data-id="element-397">
                    <PropertyInformation data-id="element-398" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-394" />
          <Route path="/rooms" element={<ProtectedRoute data-id="element-400">
                <AppLayout data-id="element-401">
                  <PageTransitionWrapper data-id="element-402">
                    <Rooms data-id="element-403" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-399" />
          <Route path="/add-room" element={<ProtectedRoute data-id="element-405">
                <AppLayout data-id="element-406">
                  <PageTransitionWrapper data-id="element-407">
                    <AddRoom data-id="element-408" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-404" />
          <Route path="/edit-room" element={<ProtectedRoute data-id="element-410">
                <AppLayout data-id="element-411">
                  <PageTransitionWrapper data-id="element-412">
                    <EditRoom data-id="element-413" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-409" />
          <Route path="/room-detail" element={<ProtectedRoute data-id="element-415">
                <AppLayout data-id="element-416">
                  <PageTransitionWrapper data-id="element-417">
                    <RoomDetail data-id="element-418" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-414" />
          <Route path="/inventory" element={<ProtectedRoute data-id="element-420">
                <AppLayout data-id="element-421">
                  <PageTransitionWrapper data-id="element-422">
                    <Inventory data-id="element-423" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-419" />
          <Route path="/rates" element={<ProtectedRoute data-id="element-425">
                <AppLayout data-id="element-426">
                  <PageTransitionWrapper data-id="element-427">
                    <Rates data-id="element-428" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-424" />
          <Route path="/reservations" element={<ProtectedRoute data-id="element-430">
                <AppLayout data-id="element-431">
                  <PageTransitionWrapper data-id="element-432">
                    <Reservations data-id="element-433" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-429" />
          <Route path="/reservation-detail" element={<ProtectedRoute data-id="element-435">
                <AppLayout data-id="element-436">
                  <PageTransitionWrapper data-id="element-437">
                    <ReservationDetail data-id="element-438" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-434" />
          <Route path="/invoices" element={<ProtectedRoute data-id="element-440">
                <AppLayout data-id="element-441">
                  <PageTransitionWrapper data-id="element-442">
                    <Invoices data-id="element-443" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-439" />
          <Route path="/invoice-detail/:id" element={<ProtectedRoute data-id="element-445">
                <AppLayout data-id="element-446">
                  <PageTransitionWrapper data-id="element-447">
                    <InvoiceDetail data-id="element-448" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-444" />
          <Route path="/invoice-form" element={<ProtectedRoute data-id="element-450">
                <AppLayout data-id="element-451">
                  <PageTransitionWrapper data-id="element-452">
                    <InvoiceForm data-id="element-453" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-449" />
          <Route path="/outlets" element={<ProtectedRoute data-id="element-455">
                <AppLayout data-id="element-456">
                  <PageTransitionWrapper data-id="element-457">
                    <PortfolioDashboard data-id="element-458" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-454" />
          <Route path="/outlets/directory" element={<ProtectedRoute data-id="element-460">
                <AppLayout data-id="element-461">
                  <PageTransitionWrapper data-id="element-462">
                    <OutletDirectory data-id="element-463" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-459" />
          <Route path="/outlets/mall/:id" element={<ProtectedRoute data-id="element-465">
                <AppLayout data-id="element-466">
                  <PageTransitionWrapper data-id="element-467">
                    <MallView data-id="element-468" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-464" />
          <Route path="/outlets/outlet/:id" element={<ProtectedRoute data-id="element-470">
                <AppLayout data-id="element-471">
                  <PageTransitionWrapper data-id="element-472">
                    <OutletDetail data-id="element-473" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-469" />
          <Route path="/outlets/reconciliation" element={<ProtectedRoute data-id="element-475">
                <AppLayout data-id="element-476">
                  <PageTransitionWrapper data-id="element-477">
                    <Reconciliation data-id="element-478" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-474" />
          <Route path="/outlets/reports" element={<ProtectedRoute data-id="element-480">
                <AppLayout data-id="element-481">
                  <PageTransitionWrapper data-id="element-482">
                    <OutletReports data-id="element-483" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-479" />
          <Route path="/outlets/onboard" element={<ProtectedRoute data-id="element-485">
                <AppLayout data-id="element-486">
                  <PageTransitionWrapper data-id="element-487">
                    <OutletOnboarding data-id="element-488" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-484" />
          <Route path="/customers" element={<ProtectedRoute data-id="element-490">
                <AppLayout data-id="element-491">
                  <PageTransitionWrapper data-id="element-492">
                    <Customers data-id="element-493" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-489" />
          <Route path="/customer-detail/:id" element={<ProtectedRoute data-id="element-495">
                <AppLayout data-id="element-496">
                  <PageTransitionWrapper data-id="element-497">
                    <CustomerDetail data-id="element-498" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-494" />
          <Route path="/customer-form" element={<ProtectedRoute data-id="element-500">
                <AppLayout data-id="element-501">
                  <PageTransitionWrapper data-id="element-502">
                    <CustomerForm data-id="element-503" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-499" />
          <Route path="/sales" element={<ProtectedRoute data-id="element-505">
                <AppLayout data-id="element-506">
                  <PageTransitionWrapper data-id="element-507">
                    <Sales data-id="element-508" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-504" />
          <Route path="/sale-detail/:id" element={<ProtectedRoute data-id="element-510">
                <AppLayout data-id="element-511">
                  <PageTransitionWrapper data-id="element-512">
                    <SaleDetail data-id="element-513" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-509" />
          <Route path="/balance" element={<ProtectedRoute data-id="element-515">
                <AppLayout data-id="element-516">
                  <PageTransitionWrapper data-id="element-517">
                    <BalanceSummary data-id="element-518" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-514" />
          <Route path="/balance-activity" element={<ProtectedRoute data-id="element-520">
                <AppLayout data-id="element-521">
                  <PageTransitionWrapper data-id="element-522">
                    <BalanceActivity data-id="element-523" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-519" />
          <Route path="/balance-activity/:id" element={<ProtectedRoute data-id="element-525">
                <AppLayout data-id="element-526">
                  <PageTransitionWrapper data-id="element-527">
                    <BalanceActivityDetail data-id="element-528" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-524" />
          <Route path="/payouts" element={<ProtectedRoute data-id="element-530">
                <AppLayout data-id="element-531">
                  <PageTransitionWrapper data-id="element-532">
                    <Payouts data-id="element-533" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-529" />
          <Route path="/payouts/:id" element={<ProtectedRoute data-id="element-535">
                <AppLayout data-id="element-536">
                  <PageTransitionWrapper data-id="element-537">
                    <PayoutDetail data-id="element-538" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-534" />
          <Route path="/suppliers" element={<ProtectedRoute data-id="element-540">
                <AppLayout data-id="element-541">
                  <PageTransitionWrapper data-id="element-542">
                    <Suppliers data-id="element-543" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-539" />
          <Route path="/supplier-detail/:id" element={<ProtectedRoute data-id="element-545">
                <AppLayout data-id="element-546">
                  <PageTransitionWrapper data-id="element-547">
                    <SupplierDetail data-id="element-548" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-544" />
          <Route path="/supplier-form" element={<ProtectedRoute data-id="element-550">
                <AppLayout data-id="element-551">
                  <PageTransitionWrapper data-id="element-552">
                    <SupplierForm data-id="element-553" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-549" />
          <Route path="/supplier-bulk-upload" element={<ProtectedRoute data-id="element-555">
                <AppLayout data-id="element-556">
                  <PageTransitionWrapper data-id="element-557">
                    <BulkSupplierUpload data-id="element-558" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-554" />
          <Route path="/bill-bulk-upload" element={<ProtectedRoute data-id="element-560">
                <AppLayout data-id="element-561">
                  <PageTransitionWrapper data-id="element-562">
                    <BulkBillUpload data-id="element-563" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-559" />
          <Route path="/bills" element={<ProtectedRoute data-id="element-565">
                <AppLayout data-id="element-566">
                  <PageTransitionWrapper data-id="element-567">
                    <Bills data-id="element-568" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-564" />
          <Route path="/bills/financing" element={<ProtectedRoute data-id="element-570">
                <AppLayout data-id="element-571">
                  <PageTransitionWrapper data-id="element-572">
                    <Financing data-id="element-573" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-569" />
          <Route path="/bills/financing/apply" element={<ProtectedRoute data-id="element-575">
                <FinancingApplyWizard data-id="element-576" />
              </ProtectedRoute>} data-id="element-574" />
          <Route path="/bills/financing/facility/:id" element={<ProtectedRoute data-id="element-578">
                <AppLayout data-id="element-579">
                  <PageTransitionWrapper data-id="element-580">
                    <FacilityDetail data-id="element-581" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-577" />
          <Route path="/bill-detail/:id" element={<ProtectedRoute data-id="element-583">
                <AppLayout data-id="element-584">
                  <PageTransitionWrapper data-id="element-585">
                    <BillDetail data-id="element-586" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-582" />
          <Route path="/bill-form" element={<ProtectedRoute data-id="element-588">
                <AppLayout data-id="element-589">
                  <PageTransitionWrapper data-id="element-590">
                    <BillForm data-id="element-591" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-587" />
          <Route path="/bill-approval/:id" element={<ProtectedRoute data-id="element-593">
                <AppLayout data-id="element-594">
                  <PageTransitionWrapper data-id="element-595">
                    <BillApprovalWizard data-id="element-596" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-592" />
          <Route path="/bill-financing/:id" element={<ProtectedRoute data-id="element-598">
                <AppLayout data-id="element-599">
                  <PageTransitionWrapper data-id="element-600">
                    <BillFinancingWizard data-id="element-601" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-597" />
          <Route path="/disputes" element={<ProtectedRoute data-id="element-603">
                <AppLayout data-id="element-604">
                  <PageTransitionWrapper data-id="element-605">
                    <Disputes data-id="element-606" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-602" />
          <Route path="/dispute-detail/:id" element={<ProtectedRoute data-id="element-608">
                <AppLayout data-id="element-609">
                  <PageTransitionWrapper data-id="element-610">
                    <DisputeDetail data-id="element-611" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-607" />
          <Route path="/disputes/:id" element={<ProtectedRoute data-id="element-613">
                <AppLayout data-id="element-614">
                  <PageTransitionWrapper data-id="element-615">
                    <DisputeDetail data-id="element-616" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-612" />
          <Route path="/disputes/:id/challenge" element={<ProtectedRoute data-id="element-618">
                <ChallengeDispute data-id="element-619" />
              </ProtectedRoute>} data-id="element-617" />
          <Route path="/refunds" element={<ProtectedRoute data-id="element-621">
                <AppLayout data-id="element-622">
                  <PageTransitionWrapper data-id="element-623">
                    <Refunds data-id="element-624" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-620" />
          <Route path="/refund-detail/:id" element={<ProtectedRoute data-id="element-626">
                <AppLayout data-id="element-627">
                  <PageTransitionWrapper data-id="element-628">
                    <RefundDetail data-id="element-629" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-625" />
          <Route path="/custom-metrics" element={<ProtectedRoute data-id="element-631">
                <AppLayout data-id="element-632">
                  <PageTransitionWrapper data-id="element-633">
                    <CustomMetrics data-id="element-634" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-630" />
          <Route path="/nadi" element={<ProtectedRoute data-id="element-636">
                <AppLayout data-id="element-637">
                  <PageTransitionWrapper data-id="element-638">
                    <Nadi data-id="element-639" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-635" />
          <Route path="/reports" element={<ProtectedRoute data-id="element-641">
                <AppLayout data-id="element-642">
                  <PageTransitionWrapper data-id="element-643">
                    <Reports data-id="element-644" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-640" />
          <Route path="/card-monitoring" element={<ProtectedRoute data-id="element-646">
                <AppLayout data-id="element-647">
                  <PageTransitionWrapper data-id="element-648">
                    <CardMonitoringPrograms data-id="element-649" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-645" />
          <Route path="/card-monitoring/vamp" element={<ProtectedRoute data-id="element-651">
                <AppLayout data-id="element-652">
                  <PageTransitionWrapper data-id="element-653">
                    <VampDetail data-id="element-654" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-650" />
          <Route path="/payment-analytics" element={<ProtectedRoute data-id="element-656">
                <AppLayout data-id="element-657">
                  <PageTransitionWrapper data-id="element-658">
                    <PaymentAnalytics data-id="element-659" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-655" />
          <Route path="/activity-logs" element={<ProtectedRoute data-id="element-661">
                <AppLayout data-id="element-662">
                  <PageTransitionWrapper data-id="element-663">
                    <ActivityLogs data-id="element-664" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-660" />
          <Route path="/requests" element={<ProtectedRoute data-id="element-666">
                <AppLayout data-id="element-667">
                  <PageTransitionWrapper data-id="element-668">
                    <Requests data-id="element-669" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-665" />
          <Route path="/requests/:id" element={<ProtectedRoute data-id="element-671">
                <AppLayout data-id="element-672">
                  <PageTransitionWrapper data-id="element-673">
                    <RequestDetail data-id="element-674" />
                  </PageTransitionWrapper>
                </AppLayout>
              </ProtectedRoute>} data-id="element-670" />
        </Routes>
      </Suspense>
    </BrowserRouter>;
}